import { apiFetch } from '../api';

// ============================================================================
// API per operazioni su SINGOLE ÓRDENES (/order)
// ============================================================================

/**
 * Obtiene las órdenes asignadas a un usuario (y/o su grupo)
 * @param {string} username
 * @param {number} state - estado de la orden (1=Nuevo, 2=En progreso, etc.)
 * @param {number} assign - tipo de asignación (0=usuario, 1=grupo, 2=ambos)
 */
export async function getOrders(username, state = -1, assign = 2) {
  //const res = await apiFetch(`/orders?username=${username}&state=${state}&assign=${assign}`);
  let query = `/orders?username=${username}&assign=${assign}`;
  if (state !== -1 && state !== undefined && state !== null) {
    query += `&state=${state}`;
  }

  const res = await apiFetch(query);
  if (!res.ok) throw new Error("No se pudieron cargar las órdenes");
  return await res.json();
}

/**
 * Obtiene una orden específica por su COD_ID
 * @param {string} codId - ID único del parte
 */
export async function getOrder(codId) {
  const res = await apiFetch(`/order?ID=${codId}`);
  if (!res.ok) throw new Error("No se pudo cargar la orden");
  const data = await res.json();

  // Si devuelve un array vacío, la orden no existe o no tiene permisos
  if (Array.isArray(data) && data.length === 0) {
    throw new Error("Orden no encontrada o sin permisos");
  }

  return data;
}

/**
 * Obtiene el reporte de una orden terminada
 * @param {string} codId - ID único del parte
 */
export async function getOrderReport(codId) {
  const res = await apiFetch(`/order/report?ID=${codId}`);
  if (!res.ok) throw new Error("No se pudo cargar el reporte");
  const data = await res.json();

  // Si devuelve un array vacío, la orden no existe, no tiene permisos o no está terminada
  if (Array.isArray(data) && data.length === 0) {
    throw new Error("Reporte no disponible (orden no terminada o sin permisos)");
  }

  return data;
}

/**
 * Inicia un parte (estado 1 → 2) con PUT /order
 * Solo se requiere el COD_ID
 * @param {string} codId - ID único del parte
 */
export async function startOrder(codId) {
  const res = await apiFetch(`/order`, {
    method: "PUT",
    body: JSON.stringify({ COD_ID: codId })
  });

  const data = await res.json();
  console.log("Respuesta de startOrder:", data);

  if (!res.ok || data.status !== "OK") {
    throw new Error(data.status);
  }

  return data;
}

/**
 * Elimina un parte en estado Nuevo (estado 1)
 * @param {string} codId - ID único del parte
 * @param {string} codCliente - ID único del cliente
 */
export async function deleteOrder(codId, codCliente) {
  const res = await apiFetch(`/order`, {
    method: "PATCH",
    body: JSON.stringify({
      COD_ID: codId,
      COD_CLIENTE: codCliente
    })
  });

  const data = await res.json();

  if (!res.ok || data.status !== "OK") {
    throw new Error(data.status);
  }

  return data;
}

/**
 * Reasigna un parte a un grupo de usuarios
 * @param {string} codId - ID único del parte
 * @param {string} codCliente - ID único del cliente
 * @param {string} codIdUserGroup - ID del grupo al que asignar
 */
export async function reassignOrderToGroup(codId, codCliente, codIdUserGroup) {
  const res = await apiFetch(`/order`, {
    method: "POST",
    body: JSON.stringify({
      COD_ID: codId,
      COD_CLIENTE: codCliente,
      COD_ID_USER_GROUP: codIdUserGroup
    })
  });

  const data = await res.json();

  if (!res.ok || data.status !== "OK") {
    throw new Error(data.status);
  }

  return data;
}

/**
 * Reasigna un parte a un usuario específico
 * @param {string} codId - ID único del parte
 * @param {string} codCliente - ID único del cliente
 * @param {string} codIdUser - ID del usuario al que asignar
 */
export async function reassignOrderToUser(codId, codCliente, codIdUser) {
  const res = await apiFetch(`/order`, {
    method: "POST",
    body: JSON.stringify({
      COD_ID: codId,
      COD_CLIENTE: codCliente,
      COD_ID_USER: codIdUser
    })
  });

  const data = await res.json();

  if (!res.ok || data.status !== "OK") {
    throw new Error(data.status);
  }

  return data;
}

/**
 * Reasigna un parte (versión genérica)
 * @param {string} codId - ID único del parte
 * @param {string} codCliente - ID único del cliente
 * @param {Object} assignment - Objeto con COD_ID_USER o COD_ID_USER_GROUP
 */
export async function reassignOrder(codId, codCliente, assignment) {
  if (assignment.COD_ID_USER_GROUP) {
    return reassignOrderToGroup(codId, codCliente, assignment.COD_ID_USER_GROUP);
  } else if (assignment.COD_ID_USER) {
    return reassignOrderToUser(codId, codCliente, assignment.COD_ID_USER);
  } else {
    throw new Error("Debe especificar COD_ID_USER o COD_ID_USER_GROUP");
  }
}


// ============================================================================
// API per operazioni MULTIPLE / BULK su ÓRDENES (/orders)
// ============================================================================

/**
 * Obtiene los reportes de múltiples órdenes terminadas
 * @param {string} username - Nombre del usuario
 * @param {number} reported - Tipo de lista (0=no reportadas, 1=reportadas, 2=todas)
 */
export async function getOrdersReports(username, reported = 2) {
  const res = await apiFetch(`/orders/report?username=${username}&Reported=${reported}`);
  if (!res.ok) throw new Error("No se pudieron cargar los reportes");
  return await res.json();
}

/**
 * Actualiza el estado y comentarios de múltiples órdenes
 * @param {Array} orders - Array de órdenes con {COD_ID, Estado?, comments?, Terminado?}
 * @returns {Object} Resultado de la operación
 * 
 * Ejemplo:
 * [
 *   {
 *     COD_ID: "72",
 *     Estado: "4",
 *     Comments: ["RUIDO : NO", "<Report>...</Report>"],
 *     Terminado: "2025-06-24T00:00:00.000+02:00"
 *   }
 * ]
 */
export async function updateOrders(orders) {
  const res = await apiFetch(`/orders`, {
    method: "PUT",
    body: JSON.stringify(orders)
  });

  const data = await res.json();

  if (!res.ok) {
    throw new Error(data.status);
  }

  return data;
}

/**
 * Crea múltiples órdenes nuevas
 * @param {Array} orders - Array de órdenes a crear
 * @returns {Object} {status: "número", actualizadas: ["CUPS1", "CUPS2", ...]}
 * 
 * Cada orden debe contener:
 * - COD_ID_GROUP (obligatorio)
 * - COD_ID_USER o COD_ID_USER_GROUP (uno de los dos)
 * - TipoParte (obligatorio)
 * - CUPS (obligatorio)
 * - CntId (obligatorio)
 * - DIRECCIONSUMINISTRO (obligatorio)
 * - COORDENADA_X, COORDENADA_Y (obligatorios, WGS 84)
 * - Te (obligatorio)
 * - TIPO_INSTALACION (obligatorio)
 * - PARAMS (array, obligatorio)
 * - NOMBRECT, RUTA (opcionales)
 * 
 * Ejemplo:
 * [
 *   {
 *     COD_ID_GROUP: "21",
 *     COD_ID_USER: "26",
 *     TipoParte: "1",
 *     CUPS: "ES0140000000033217PV",
 *     CntId: "AUX2300115797",
 *     NOMBRECT: "CT ESCRIBANO",
 *     DIRECCIONSUMINISTRO: "C/ ANTONIO DEL REY. 101",
 *     RUTA: "RUTA DEL CT ESCRIBANO Norte",
 *     Te: "5",
 *     TIPO_INSTALACION: "Monofasica",
 *     COORDENADA_X: "38.47395192",
 *     COORDENADA_Y: "-4.67905624",
 *     PARAMS: ["20TD","A","AUTOCONSUMO","4600","0","4600","0","0","0","SI","5000"]
 *   }
 * ]
 */
export async function createOrders(orders) {
  const res = await apiFetch(`/orders`, {
    method: "POST",
    body: JSON.stringify(orders)
  });

  const data = await res.json();

  if (!res.ok) {
    throw new Error(data.status);
  }

  // Retorna: {status: "2", actualizadas: ["CUPS1", "CUPS2"]}
  return data;
}

/**
 * Obtiene todos los tipos de partes disponibles con sus campos
 * @returns {Object} Información de todos los tipos de partes
 * 
 * Incluye:
 * - TipoParte: ID del tipo
 * - Descripción del tipo
 * - Campos PARAMS necesarios para cada tipo
 * - Campos Comments usados por la App
 */
export async function getOrderTypes() {
  const res = await apiFetch(`/orders/types`);
  if (!res.ok) throw new Error("No se pudieron cargar los tipos de partes");
  return await res.json();
}
/**
 * Recupera los esquemas JSON que describen los diferentes tipos de órdenes.
 * Cada esquema se utilizará para construir el formulario dinámico en la creación de partes.
 */
export async function getOrderSchemas() {
  const res = await apiFetch(`/orders/schemas`);
  if (!res.ok) {
    const text = await res.text().catch(() => "(sin cuerpo)");
    throw new Error(`Falló /orders/schemas (${res.status}): ${text}`);
  }
  return await res.json();
}

/**
 * Obtiene los datos del ERP para un punto de medida (CUPS)
 * Útil para acelerar la creación de partes obteniendo valores actuales
 * @param {string} cups - CUPS del punto de medida
 * @returns {Object} Datos del ERP incluyendo tarifa, situación, potencias, etc.
 */
export async function getERPData(cups, codCliente) {
  const res = await apiFetch(`/erp?COD_CLIENTE=${codCliente}&CUPS=${cups}`);
  if (!res.ok) throw new Error("No se pudieron cargar los datos del ERP");
  const data = await res.json();

  if (Array.isArray(data) && data.length === 0) {
    throw new Error("CUPS no encontrado en el ERP o sin permisos");
  }

  return data;
}

/**
 * Obtiene la lista de CUPS del ERP de un cliente
 * @param {string} codCliente - ID del cliente
 */
export async function getERPCups(codCliente) {
  const res = await apiFetch(`/erp/cups?COD_CLIENTE=${codCliente}`);
  if (!res.ok) throw new Error("No se pudieron cargar los CUPS del ERP");
  return await res.json();
}

/**
 * Obtiene información ampliada del ERP para crear un parte
 * @param {string} codCliente - ID del cliente
 * @param {string} cups - CUPS del punto de medida (opcional)
 */
export async function getERPPartes(codCliente, cups = null) {
  const url = cups
    ? `/erp/partes?COD_CLIENTE=${codCliente}&CUPS=${cups}`
    : `/erp/partes?COD_CLIENTE=${codCliente}`;

  const res = await apiFetch(url);
  if (!res.ok) throw new Error("No se pudieron cargar los datos del ERP");
  const data = await res.json();

  if (Array.isArray(data) && data.length === 0) {
    throw new Error("No se encontraron datos en el ERP");
  }

  return data;
}

export async function getERPContadores(codCliente, cups = null) {
  const url = cups
    ? `/erp/partes?COD_CLIENTE=${codCliente}&CUPS=${cups}`
    : `/erp/partes?COD_CLIENTE=${codCliente}`;

  const res = await apiFetch(url);
  if (!res.ok) throw new Error("No se pudieron cargar los datos del ERP");
  const data = await res.json();

  if (Array.isArray(data) && data.length === 0) {
    throw new Error("No se encontraron datos en el ERP");
  }

  return data;
}

/**
 * Obtiene la lista de contadores disponibles para generar nuevas órdenes.
 * Permite filtrar por datos faltantes (s02, s04, s05) y rangos de fechas.
 *
 * @param {string|number} codCliente - Identificador del cliente.
 * @param {Object} [options]
 * @param {string} [options.datosFaltantes] - Tipo de dato faltante (s02, s04, s05).
 * @param {string} [options.fechaInicio] - Fecha inicio en formato ISO (YYYY-MM-DD).
 * @param {string} [options.fechaFin] - Fecha fin en formato ISO (YYYY-MM-DD).
 */
export async function getOrdersMeters(
  codCliente,
  { datosFaltantes, fechaInicio, fechaFin } = {}
) {
  if (!codCliente) {
    throw new Error("Debe especificarse el código de cliente para consultar contadores");
  }

  const params = new URLSearchParams();
  params.append("cod_cliente", String(codCliente));

  if (datosFaltantes) {
    params.append("datos_faltantes", datosFaltantes);
    params.append("datos", datosFaltantes);
  }

  if (fechaInicio) {
    params.append("fecha_inicio", fechaInicio);
  }

  if (fechaFin) {
    params.append("fecha_fin", fechaFin);
  }

  const query = params.toString();
  const res = await apiFetch(`/orders/meters?${query}`);

  if (!res.ok) {
    throw new Error("No se pudieron cargar los contadores disponibles");
  }

  return await res.json();
}

export async function getOrdersMeters02s04s05(
  codCliente,
  { datosFaltantes, fechaInicio, fechaFin } = {}
) {
  if (!codCliente) {
    throw new Error("Debe especificarse el código de cliente para consultar contadores");
  }

  const params = new URLSearchParams();
  params.append("cod_cliente", String(codCliente));

  if (datosFaltantes) {
    params.append("datos_faltantes", datosFaltantes);
    params.append("datos", datosFaltantes);
  }

  if (fechaInicio) {
    params.append("fecha_inicio", fechaInicio);
  }

  if (fechaFin) {
    params.append("fecha_fin", fechaFin);
  }

  const query = params.toString();
  const res = await apiFetch(`/orders/meters?${query}`);

  if (!res.ok) {
    throw new Error("No se pudieron cargar los contadores disponibles");
  }

  return await res.json();
}
/**
 * Obtiene la información de licencias Prime Sagemcom de un cliente.
 * Si se especifica un CUPS, devuelve únicamente la licencia asociada a dicho punto.
 *
 * @param {string|number} codCliente - Identificador del cliente.
 * @param {string} [cups] - CUPS específico a consultar.
 */
export async function getPrimeSagemLicenses(codCliente, cups = null) {
  if (!codCliente) {
    throw new Error("Debe especificarse el código de cliente para consultar licencias Prime");
  }

  const params = new URLSearchParams();
  params.append("COD_CLIENTE", String(codCliente));

  if (cups) {
    params.append("CUPS", cups);
  }

  const query = params.toString();
  const res = await apiFetch(`/erp/primesagem?${query}`);

  const data = await res.json();

  if (!res.ok) {
    throw new Error("No se pudieron cargar las licencias Prime Sagemcom");
  }

  if (Array.isArray(data) && data.length === 0) {
    return [];
  }

  return data;
}

export async function getERPValidation(codCliente, cups) {
  if (!codCliente || !cups) {
    throw new Error("Debe especificarse COD_CLIENTE y CUPS para la validación ERP");
  }

  const url = `/erp/validation?COD_CLIENTE=${codCliente}&CUPS=${cups}`;
  const res = await apiFetch(url);
  if (!res.ok) {
    throw new Error("No se pudieron cargar los datos de validación ERP");
  }

  const data = await res.json();

  if (Array.isArray(data) && data.length === 0) {
    return [];
  }
  return data;
}

/**
 * Modifica un parte existente usando POST /order con el payload completo.
 * Este endpoint permite reasignar y actualizar datos como dirección,
 * coordenadas o parámetros.
 * @param {Object} payload - Debe incluir COD_ID y COD_ID_GROUP
 * @returns {Object} Resultado de la operación
 */
export async function updateOrder(payload) {
  const res = await apiFetch(`/order`, {
    method: "POST",
    body: JSON.stringify(payload)
  });

  const data = await res.json();

  if (!res.ok || data.status !== "OK") {
    throw new Error(data.status || "Error");
  }

  return data;
}